import { AICompletionType, FlashcardSet, ItemType, SubscriptionType } from "@/graphql/schema";
import { AI_PROMPT_OUTPUT_MAP, standaloneCallAI } from "./utils";
import { verifyAIUsage } from "./genericUtils";
import { mutate } from "swr";
import { LocalAuthUser } from "../user/types";
import { StableSWRKeys } from "../swr/swr";
import { AI_PROMPT_PARAMS_MAP } from "@knowt/syncing/hooks/ai/aiPrompts/types";
import { resolveFlashcardSetSWRKey } from "../flashcards/utils";
import { platform } from "@/platform";
import { htmlToMd } from "@knowt/editor/md/parsing/parse";

export const useAICopilot = ({ itemId, itemType }: { itemId: string; itemType: ItemType }) => {
    const maybeReachedAIUsage = async (type: AICompletionType) => {
        const localUser = await mutate<LocalAuthUser>(StableSWRKeys.USER, user => user, { revalidate: false });
        verifyAIUsage(localUser?.user, type);

        const mixpanel = await platform.analytics.mixpanel();

        if (
            localUser?.user?.ai?.curPrompts === 35 &&
            (localUser?.user?.subscriptionType === SubscriptionType.BASIC || !localUser?.user?.subscriptionType)
        ) {
            mixpanel.track("Kai Complete - Limit Reached ", {
                noteId: itemType === ItemType.NOTE ? itemId : undefined,
                flashcardSetId: itemType === ItemType.FLASHCARDSET ? itemId : undefined,
            });
        }
    };

    const getSuggestion = async <T extends keyof AI_PROMPT_OUTPUT_MAP>({
        type,
        aiParams,
    }: {
        type: T;
        aiParams: AI_PROMPT_PARAMS_MAP[T];
    }) => {
        try {
            await maybeReachedAIUsage(AICompletionType.FLASHCARD_AUTOCOMPLETE);
        } catch {
            return "";
        }

        const res = await standaloneCallAI({
            type,
            itemId,
            itemType,
            aiParams,
        });

        return res.parsedResponse ? (res.parsedResponse.text as string) : "";
    };

    return {
        getSuggestion,
    };
};

export const getAIFlashcardsContext = async ({
    flashcardSetId,
    currFlashcardIdx,
    overrideDom = false,
}: {
    flashcardSetId: string;
    currFlashcardIdx: number;
    overrideDom?: boolean;
}) => {
    const flashcardSet = await mutate<FlashcardSet>(
        resolveFlashcardSetSWRKey({ flashcardSetId }),
        flashcardSet => flashcardSet,
        { revalidate: false }
    );

    const flashcards = flashcardSet?.flashcards;
    if (!flashcards) {
        return { flashcards: [] as { term: string; definition: string }[], title: flashcardSet.title || "" };
    }

    const recentFlashcards = flashcards
        .slice(Math.max(0, currFlashcardIdx - 3), currFlashcardIdx)
        .filter(Boolean)
        .map(f => ({
            term: htmlToMd(f?.term || "", overrideDom),
            definition: htmlToMd(f?.definition || "", overrideDom),
        })) as { term: string; definition: string }[];

    return { flashcards: recentFlashcards, title: flashcardSet.title || "" };
};
